import { ProcedureType } from "../../../../../autogen/bff-api";

// Mirrors ProcedureType.isTwoStage in the BFF: procedures with a prequalification round
// (forespørsel om å delta → kvalifiser → inviter) before the tender phase.
export const TWO_STAGE_PROCEDURES: ReadonlySet<ProcedureType> = new Set([
  ProcedureType.Restricted,
  ProcedureType.RestrictedTenderCompetition,
  ProcedureType.NegotiatedWithPriorCall,
  ProcedureType.CompetitiveDialogue,
  ProcedureType.InnovationPartnership,
  ProcedureType.CompetitiveNegotiation,
  ProcedureType.DynamicPurchasingSystem,
  ProcedureType.QualificationScheme,
  ProcedureType.DesignContestRestricted,
]);

// Mirrors ProcedureType.isReadOnlyUntilNextRelease in the BFF, which refuses a draft patch naming one.
export const PROCEDURES_READ_ONLY_UNTIL_NEXT_RELEASE: ReadonlySet<ProcedureType> = new Set([
  ProcedureType.OpenTenderCompetition,
  ProcedureType.RestrictedTenderCompetition,
]);

export const isDesignContest = (procedureType: ProcedureType | null | undefined): boolean =>
  procedureType === ProcedureType.DesignContestOpen || procedureType === ProcedureType.DesignContestRestricted;

export const runsBelowEeaThreshold = (procedureType: ProcedureType | null | undefined): boolean =>
  procedureType === ProcedureType.OpenTenderCompetition || procedureType === ProcedureType.RestrictedTenderCompetition;

export const isTwoStageProcedure = (procedureType: ProcedureType | null | undefined): boolean =>
  procedureType != null && TWO_STAGE_PROCEDURES.has(procedureType);

// The system itself (FOA kap. 26) — announced once, then open for its whole validity.
export const isDpsScheme = (procedureType: ProcedureType | null | undefined): boolean =>
  procedureType === ProcedureType.DynamicPurchasingSystem;

// One purchase under the system (§ 26-7) — invited-only, never announced.
export const isDpsCompetition = (procedureType: ProcedureType | null | undefined): boolean =>
  procedureType === ProcedureType.DpsCompetition;

// The utilities' counterpart (forsyningsforskriften § 13-1) — announced once, open-ended intake,
// no request deadline at all.
export const isQualificationScheme = (procedureType: ProcedureType | null | undefined): boolean =>
  procedureType === ProcedureType.QualificationScheme;

export const isSchemeProcedure = (procedureType: ProcedureType | null | undefined): boolean =>
  isDpsScheme(procedureType) || isQualificationScheme(procedureType);

// § 26-7: the announcement of the system settled classification, regime and scope for every
// purchase under it, so the purchase asks for none of it again.
export const inheritsScopeFromScheme = (
  publicContext: { procedureType?: ProcedureType | null; governingSchemeId?: string | null } | null | undefined,
): boolean => isDpsCompetition(publicContext?.procedureType) || publicContext?.governingSchemeId != null;

// A competition under a system starts at the invitation to tender: the system's admission was
// its request stage (forsyningsforskriften § 13-3).
export const hasRequestStage = (
  publicContext:
    | { procedureType?: ProcedureType | null; governingSchemeId?: string | null; priorInformationCall?: unknown }
    | null
    | undefined,
): boolean =>
  isTwoStageProcedure(publicContext?.procedureType) &&
  !isQualificationScheme(publicContext?.procedureType) &&
  !inheritsScopeFromScheme(publicContext) &&
  publicContext?.priorInformationCall == null;

// A system signs nothing itself — the contract period belongs to each purchase under it.
export const signsItsOwnContract = (procedureType: ProcedureType | null | undefined): boolean =>
  !isSchemeProcedure(procedureType);

// Mirrors SourcingEventV2.hasTenderStageQualification in the BFF: single-stage public procedures
// have no prequalification round, so the suppliers are qualified on their tenders before the
// evaluation opens.
export const hasTenderStageQualification = (
  procedureType: ProcedureType | null | undefined,
): boolean => procedureType != null && !TWO_STAGE_PROCEDURES.has(procedureType);

// Mirrors ProcedureType.allowsNegotiation in the BFF: FOA § 23-6 bars negotiation and revised
// tenders in the anbudskonkurransene (åpen and begrenset).
export const NEGOTIATING_PROCEDURES: ReadonlySet<ProcedureType> = new Set([
  ProcedureType.NegotiatedWithPriorCall,
  ProcedureType.NegotiatedWithoutPriorCall,
  ProcedureType.CompetitiveDialogue,
  ProcedureType.InnovationPartnership,
  ProcedureType.CompetitiveNegotiation,
]);

export const allowsNegotiation = (procedureType: ProcedureType | null | undefined): boolean =>
  procedureType != null && NEGOTIATING_PROCEDURES.has(procedureType);

// FOA § 23-7 (2) and §§ 23-8–23-9: no initial tender to award on in a dialogue or innovation partnership.
const NEVER_AWARD_ON_INITIAL_TENDERS: ReadonlySet<ProcedureType> = new Set([
  ProcedureType.CompetitiveDialogue,
  ProcedureType.InnovationPartnership,
]);

export const offersAwardOnInitialTenders = (procedureType: ProcedureType | null | undefined): boolean =>
  procedureType != null && allowsNegotiation(procedureType) && !NEVER_AWARD_ON_INITIAL_TENDERS.has(procedureType);

// Mirrors ProcedureType.requiresBestPriceQualityRatio in the BFF: FOA § 18-1 (8) awards a competitive
// dialogue and an innovation partnership on the best price-quality ratio alone.
export const requiresBestPriceQualityRatio = (procedureType: ProcedureType | null | undefined): boolean =>
  procedureType === ProcedureType.CompetitiveDialogue || procedureType === ProcedureType.InnovationPartnership;
